// deps
import { useCallback, useContext } from "react";

// internal-deps
import { useMountedRef } from "@swiggy-private/core-ui";
import { CheckoutContext, CheckoutActionTypes, recordNREvent, NEWRELIC_TABLES } from "@swiggy-private/checkout-core";

// contexts
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { JuspayContext } from "@payment-client/contexts/JuspayContext";

// helpers
import { getNewInAppUpiPaymentMethod } from "@payment-client/helpers/InAppUpiHelper";

// constants
import { OOPS_SOMETHING_WENT_WRONG } from "@payment-client/constants/TextDict";
import { INAPP_UPI_STATE, INAPP_UPI_ERROR_TITLE } from "@payment-client/constants/InAppUpi";

// hooks
import { useOnPay } from "./UseOnPay";

// analytics
import { InAppUpiContext } from "@payment-client/contexts/InAppUpiContext";

// types
import { TOAST_TYPE } from "@swiggy-private/payment-types";
import { JuspayInAppUpiResponsePayload } from "@swiggy-private/native-bridge";

interface ReturnValue {
    initiateOnboarding: VoidFunction;
    onAddBankAccountClick: VoidFunction;
}

export const useInAppUpiGroup = (): ReturnValue => {
    const isMountedRef = useMountedRef();
    const { paymentResponse, reloadPayments } = useContext(ShowPaymentContext);
    const { setCheckoutState } = useContext(CheckoutContext);
    const { isSdkAvailable } = useContext(JuspayContext);
    const { makeInAppUpiRequest } = useContext(InAppUpiContext);
    const onPay = useOnPay();

    const showErrorToast = useCallback(() => {
        recordNREvent({
            eventType: NEWRELIC_TABLES.SWIGGY_UPI_SDK_ERROR,
            isSdkAvailable: String(isSdkAvailable),
        });
        setCheckoutState({
            type: CheckoutActionTypes.GENERIC_PAYMENT_TOAST,
            payload: {
                message: INAPP_UPI_ERROR_TITLE,
                type: TOAST_TYPE.ERROR,
            },
        });
    }, [isSdkAvailable, setCheckoutState]);

    // handles onboarding data by making place order call with selected account
    const handleSdkDataAndPlaceOrder = useCallback(
        async (sdkData: JuspayInAppUpiResponsePayload["selectedAccount"], state: INAPP_UPI_STATE) => {
            const inAppUpiMethodData = getNewInAppUpiPaymentMethod(paymentResponse?.paymentGroup ?? [], state);
            if (sdkData == null) {
                throw new Error(OOPS_SOMETHING_WENT_WRONG);
            }
            if (inAppUpiMethodData.isSome()) {
                // adding reference id from SDK response to backend's payment method response
                inAppUpiMethodData.unwrap().meta["referenceId"] = sdkData?.account?.referenceId || "";
                onPay(
                    inAppUpiMethodData.unwrap(),
                    {
                        /* eslint-disable @typescript-eslint/naming-convention */
                        in_app_upi_params: {
                            account_reference_id: sdkData?.account?.referenceId || "",
                            account_type: sdkData?.account?.type || "",
                        },
                        /* eslint-enable */
                    },
                    // passing this flag as extraArbitaryData to reload the payment page if place order fails post onboarding
                    { isInAppUpiOnBoardingFlow: true },
                );
            }
        },
        [onPay, paymentResponse?.paymentGroup],
    );

    const makeInAppUpiBridgeRequest = useCallback(
        async (state: INAPP_UPI_STATE): Promise<void> => {
            if (isMountedRef.current == null) {
                return;
            }
            try {
                const juspaySDKData = await makeInAppUpiRequest(state);
                if (isMountedRef.current == null || juspaySDKData == null) {
                    return;
                }
                if (state === INAPP_UPI_STATE.ONBOARDING || state === INAPP_UPI_STATE.ADD_ACCOUNT) {
                    handleSdkDataAndPlaceOrder(juspaySDKData, state);
                }
            } catch (error) {
                reloadPayments();
                showErrorToast();
            }
        },
        [isMountedRef, makeInAppUpiRequest, handleSdkDataAndPlaceOrder, reloadPayments, showErrorToast],
    );

    const onAddBankAccountClick = useCallback(() => {
        if (!isSdkAvailable) {
            // showing a error toast if we are not able to make the request due to SDK issue or backend(clientAuthTokenData)issue
            showErrorToast();
            return;
        }
        makeInAppUpiBridgeRequest(INAPP_UPI_STATE.ADD_ACCOUNT);
    }, [isSdkAvailable, makeInAppUpiBridgeRequest, showErrorToast]);

    const initiateOnboarding = useCallback(() => {
        if (!isSdkAvailable) {
            // showing a error toast if we are not able to make the request due to SDK issue or backend(clientAuthTokenData)issue
            showErrorToast();
            return;
        }
        makeInAppUpiBridgeRequest(INAPP_UPI_STATE.ONBOARDING);
    }, [isSdkAvailable, makeInAppUpiBridgeRequest, showErrorToast]);

    return { initiateOnboarding, onAddBankAccountClick };
};
